import {Subject} from "rxjs";
import {FormProvider, UseFormReturn} from "react-hook-form";
import {NoContactInput} from "../../domain";
import React, {useCallback} from "react";
import {trackPromise, usePromiseTracker} from "react-promise-tracker";
import _ from "lodash";
import {ModalDatePicker} from "../../form-components/ModalDatePicker";
import {InitialTypeOfContactInput} from "./InitialTypeOfContactInput";
import {NumberOfContactAttemptInput} from "./NumberOfContactAttemptInput";
import {TypeOfContactInput} from "./TypeOfContactInput";
import {FatOutlineButton} from "common/component/FatOutlineButton";
import {LoadingSpinner} from "common";
import {ModalTextAreaInput} from "../../form-components/ModalTextAreaInput";
import {useNoContactValidation} from "./NoContactSubmitRequestValidation";
import {usePreSubmitValidation} from "../ClientOfferValidation";
import {ModalSelectionInput} from "../../form-components/ModalSelectionInput";
import {YesNoOptionMap} from "../../domain";

type NoContactFormProps = {
    visible$: Subject<boolean>;
    methods: UseFormReturn<NoContactInput>;
    onFinished: (formValues: NoContactInput | null) => Promise<void>;
    onCancel: () => Promise<void>;
    externalLoading: boolean;
    authorizationGuid?: string;
    skipValidation?: boolean;
}


export function NoContactForm({
                                  visible$,
                                  methods,
                                  externalLoading,
                                  onFinished,
                                  onCancel,
                                  authorizationGuid,
                                  skipValidation = false
                              }: NoContactFormProps) {
    const submitWithTracking = useCallback((formValues: NoContactInput | null) => trackPromise(onFinished(formValues)), [onFinished]);
    const { validateNoContact } = useNoContactValidation();
    const { validateBeforeSubmit } = usePreSubmitValidation();
    const {promiseInProgress} = usePromiseTracker();
    const isCallingBackend = promiseInProgress || externalLoading;

    // Watch field values for conditional rendering
    const providerWasAbleToContactClient = methods.watch('providerWasAbleToContactClient');

    const submit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        await methods.handleSubmit(async (data: NoContactInput) => {
            const onSubmitSuccess = async () => {
                const formData = _.pick(data, [
                    'initialDateOfContactAttempt',
                    'dateContactResumed',
                    'dateServiceExpectedToResumed',
                    'lastContactAttemptDate',
                    'numberOfContactAttempts',
                    'initialTypeOfContact',
                    'typeOfContact',
                    'providerWasAbleToContactClient',
                    'comments'
                ]);
                if (formData.providerWasAbleToContactClient !== 'YES') {
                    formData.dateContactResumed = '';
                    formData.dateServiceExpectedToResumed = '';
                }
                await submitWithTracking(formData);
                methods.reset();
                visible$.next(false);
            };

            if (skipValidation) {
                await onSubmitSuccess();
            } else {
                await validateBeforeSubmit(() => validateNoContact(authorizationGuid), onSubmitSuccess);
            }
        })();
    }, [methods, visible$, submitWithTracking, validateNoContact, authorizationGuid, skipValidation, validateBeforeSubmit]);

    return (
        <FormProvider {...methods}>
            <form autoComplete="off" onSubmit={submit}>
                {!isCallingBackend && <>
                    <div className={"p-2"}/>
                    <ModalDatePicker<NoContactInput>
                        disabled={isCallingBackend}
                        label={'Initial Date of Contact'}
                        field={'initialDateOfContactAttempt'}
                        optionalRules={{
                            validate: {
                                notAfterToday: (value) => {
                                    if (value) {
                                        const selectedDate = new Date(value);
                                        const today = new Date();
                                        selectedDate.setHours(0, 0, 0, 0);
                                        today.setHours(0, 0, 0, 0);
                                        if (selectedDate > today) {
                                            return '"Initial Date of Contact" cannot be after the current date';
                                        }
                                    }
                                    return true;
                                }
                            }
                        }}
                    />
                    <InitialTypeOfContactInput sending={isCallingBackend}/>
                    <NumberOfContactAttemptInput disabled={isCallingBackend}/>
                    <ModalDatePicker<NoContactInput>
                        disabled={isCallingBackend}
                        label={'Last Contact Attempt Date'}
                        field={'lastContactAttemptDate'}
                        optionalRules={{
                            validate: {
                                notAfterToday: (value) => {
                                    if (value) {
                                        const selectedDate = new Date(value);
                                        const today = new Date();
                                        selectedDate.setHours(0, 0, 0, 0);
                                        today.setHours(0, 0, 0, 0);
                                        if (selectedDate > today) {
                                            return '"Last Contact Attempt Date" cannot be after the current date';
                                        }
                                    }
                                    return true;
                                },
                                dateComparison: (value) => {
                                    const initialDate = methods.getValues('initialDateOfContactAttempt');
                                    if (value && initialDate) {
                                        const lastContactDate = new Date(value);
                                        const initialContactDate = new Date(initialDate);
                                        if (lastContactDate <= initialContactDate) {
                                            return '"Last Contact Attempt Date" cannot be the same, or, before the "Initial Date of Contact".';
                                        }
                                    }
                                    return true;
                                }
                            }
                        }}
                    />
                    <TypeOfContactInput sending={isCallingBackend}/>
                    <ModalSelectionInput<NoContactInput>
                        disabled={isCallingBackend}
                        options={YesNoOptionMap}
                        required={false}
                        label={"Provider was able to contact client"}
                        field={'providerWasAbleToContactClient'}
                    />
                    {providerWasAbleToContactClient === 'YES' && (
                        <ModalDatePicker<NoContactInput>
                            disabled={isCallingBackend}
                            label={'Date Contact Resumed'}
                            required={false}
                            field={'dateContactResumed'}
                            optionalRules={{
                                validate: {
                                    requiredIfContactMade: (value) => {
                                        if (!value) {
                                            return 'Please enter "Date Contact Resumed"';
                                        }
                                        return true;
                                    },
                                    notAfterToday: (value) => {
                                        if (value) {
                                            const selectedDate = new Date(value);
                                            const today = new Date();
                                            selectedDate.setHours(0, 0, 0, 0);
                                            today.setHours(0, 0, 0, 0);
                                            if (selectedDate > today) {
                                                return '"Date Contact Resumed" cannot be after the current date';
                                            }
                                        }
                                        return true;
                                    },
                                    dateComparison: (value) => {
                                        const initialDate = methods.getValues('initialDateOfContactAttempt');
                                        if (value && initialDate) {
                                            const resumedDate = new Date(value);
                                            const initialContactDate = new Date(initialDate);
                                            if (resumedDate <= initialContactDate) {
                                                return '"Date Contact Resumed" cannot be the same, or, before the "Initial Date of Contact".';
                                            }
                                        }
                                        return true;
                                    }
                                }
                            }}
                        />
                    )}
                    {providerWasAbleToContactClient === 'YES' && (
                        <ModalDatePicker<NoContactInput>
                            disabled={isCallingBackend}
                            label={'Date Service Expected to Resume'}
                            required={false}
                            field={'dateServiceExpectedToResumed'}
                            optionalRules={{
                                validate: {
                                    requiredIfDateResumed: (value) => {
                                        const dateContactResumed = methods.getValues('dateContactResumed');
                                        if (dateContactResumed && !value) {
                                            return 'Please enter "Date Service Expected to Resume"';
                                        }
                                        return true;
                                    },
                                    dateComparisonWithInitial: (value) => {
                                        const initialDate = methods.getValues('initialDateOfContactAttempt');
                                        if (value && initialDate) {
                                            const expectedDate = new Date(value);
                                            const initialContactDate = new Date(initialDate);
                                            if (expectedDate <= initialContactDate) {
                                                return '"Date Service Expected to Resume" cannot be the same, or, before the "Initial Date of Contact".';
                                            }
                                        }
                                        return true;
                                    },
                                    dateComparisonWithResumed: (value) => {
                                        const dateContactResumed = methods.getValues('dateContactResumed');
                                        if (value && dateContactResumed) {
                                            const expectedDate = new Date(value);
                                            const resumedDate = new Date(dateContactResumed);
                                            expectedDate.setHours(0, 0, 0, 0);
                                            resumedDate.setHours(0, 0, 0, 0);
                                            if (expectedDate < resumedDate) {
                                                return '"Date Service Expected to Resume" cannot be before the "Date Contact Resumed".';
                                            }
                                        }
                                        return true;
                                    }
                                }
                            }}
                        />
                    )}
                    <ModalTextAreaInput
                        disabled={isCallingBackend}
                        field="comments"
                        label="Comments"
                        labelPosition="left"
                        rowsNum={3}
                        maxLength={2048}
                        showCharacterCount
                        required={false}
                        inputClassName="pr-2"
                    />
                    <div className={"p-2"}/>
                    <div className="w-full flex border-t dark:border-gray-600 p-4 justify-end">
                        <div className="flex w-1/4">
                            <FatOutlineButton color={'red'}
                                              text={"Cancel"}
                                              onClick={onCancel}/>
                            <span style={{width: 10}}/>
                            <FatOutlineButton type="submit" text={"Submit"} color={"green"}/>
                        </div>
                    </div>
                </>
                }

                {isCallingBackend &&
                    <div className="p-12">
                        <LoadingSpinner text="Processing"/>
                    </div>
                }
            </form>
        </FormProvider>
    );
}
